/**
 * CancelledStrip — una reserva cancel·lada dins de la graella (`plans/calendar-visual-refresh.md`
 * §5.8, D6).
 *
 * No és una `ReservationCell` (no hi cap): és una franja baixa de la fila (10 / 6 / 4 px segons
 * l'alçada de la fila, `rowSlotStyle`, F7), perquè una cancel·lada mai tapi una viva. Fons de cancel·lada i una vora esquerra del color del canal.
 * El clic fa el mateix que el de la card (`resolveReservationClick`): ratolí → la fitxa; tàctil →
 * el popover d'informació (o la pantalla completa en pantalla estreta). No s'arrossega mai: no
 * porta `GRID_TARGET_ATTR`, només l'id de la reserva perquè `useGridPointer` la reconegui.
 */

import { useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/useIsMobile';
import { CALENDAR_PALETTE, useCalendarTheme } from '../calendar-theme';
import type { ReservationCellData } from '../ReservationCell';
import {
  ReservationFullScreen,
  ReservationInfoBody,
  resolveReservationClick,
} from '../ReservationPopover';
import {
  GRID_RESERVATION_ID_ATTR,
  normalizePointerType,
  type GridPointerType,
  type LastGesture,
} from './useGridPointer';
import type { RowSlotStyle } from './row-layout';
import { cancelledStripTitle } from './day-labels';

/** Vora esquerra (color del canal) de la franja, en px. */
const STRIP_CHANNEL_BORDER_PX = 3;

/** Els clics i `pointerdown` de dins del popover no han d'arribar a la graella. */
const stopGridEvents = {
  onClick: (e: React.MouseEvent) => e.stopPropagation(),
  onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
  onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
};

export interface CancelledStripProps {
  reservation: ReservationCellData;
  left: number;
  width: number;
  /** Lloc vertical de la franja (`rowSlotStyle(alçada, 'cancelled')`). */
  slotStyle: RowSlotStyle;
  onOpenSheet?: (id: string) => void;
  getLastGesture?: () => LastGesture;
}

export function CancelledStrip({
  reservation,
  left,
  width,
  slotStyle,
  onOpenSheet,
  getLastGesture,
}: CancelledStripProps) {
  const { channelColor } = useCalendarTheme();
  const [infoOpen, setInfoOpen] = useState(false);
  const isMobile = useIsMobile();
  const triggerRef = useRef<HTMLDivElement>(null);
  const ownPointerTypeRef = useRef<GridPointerType | null>(null);
  const title = cancelledStripTitle(reservation);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const action = resolveReservationClick(getLastGesture?.(), ownPointerTypeRef.current);
    if (action === 'ignore') return;
    if (action === 'open-sheet') {
      onOpenSheet?.(reservation.id);
      return;
    }
    setInfoOpen((open) => !open);
  };
  const closeInfo = () => setInfoOpen(false);

  const strip = (
    <div
      ref={triggerRef}
      role="button"
      aria-label={title}
      title={title}
      data-testid="cancelled-strip"
      {...{ [GRID_RESERVATION_ID_ATTR]: reservation.id }}
      onClick={handleClick}
      onPointerDown={(e) => {
        ownPointerTypeRef.current = normalizePointerType(e.pointerType);
      }}
      className="absolute cursor-pointer rounded-sm border hover:brightness-95"
      style={{
        left,
        width,
        ...slotStyle,
        backgroundColor: CALENDAR_PALETTE.cardCancelled,
        borderColor: CALENDAR_PALETTE.cardBorder,
        borderLeftColor: channelColor(reservation.channel),
        borderLeftWidth: STRIP_CHANNEL_BORDER_PX,
      }}
    />
  );

  return (
    <>
      <Popover open={infoOpen && !isMobile} onOpenChange={setInfoOpen}>
        <PopoverPrimitive.Anchor asChild>{strip}</PopoverPrimitive.Anchor>
        <PopoverContent
          className="p-3 z-50 max-h-[var(--radix-popover-content-available-height)] flex flex-col overflow-hidden"
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onInteractOutside={(e) => {
            if (e.target instanceof Node && triggerRef.current?.contains(e.target)) {
              e.preventDefault();
            }
          }}
          {...stopGridEvents}
        >
          <ReservationInfoBody data={reservation} onOpenSheet={onOpenSheet} onClose={closeInfo} />
        </PopoverContent>
      </Popover>
      {infoOpen && isMobile && (
        <ReservationFullScreen data={reservation} onOpenSheet={onOpenSheet} onClose={closeInfo} />
      )}
    </>
  );
}

export default CancelledStrip;
